/**
 * What each provider CALLS the value stored in the `efax_fax_id` column.
 *
 * WHY THIS FILE EXISTS
 *
 * The column is the PROVIDER-AGNOSTIC outbound handle and its name is not. It keeps the
 * `efax_` prefix because renaming it would migrate a unique index that inbound dedup depends
 * on, for a cosmetic gain. The cost of that decision is paid in the UI: three surfaces
 * rendered `<Row label="eFax fax ID" value={row.efax_fax_id} />`, and SFax stores its
 * `SendFaxQueueId` in that column — so every SFax transmission had its queue id displayed
 * under another provider's name. An operator reading that would quote it to the wrong support
 * desk.
 *
 * `backend/services/transmission.py::OUTBOUND_HANDLE_LABELS` is the same table in Python and
 * `tests/test_outbound_info.py` pins the two equal, the way `tests/test_label_parity.py` pins
 * the leg labels. Add a provider to one and the test fails until it is in both.
 */

import { providerLabel, PROVIDER_NOT_RECORDED } from './methods'

export const OUTBOUND_HANDLE_LABELS: Record<string, string> = {
  efax: 'eFax fax_id',
  sfax: 'SFax SendFaxQueueId',
  // SRFax's Queue_Fax returns `Result` as the FaxDetailsID, stored in this same column.
  srfax: 'SRFax FaxDetailsID',
}

/**
 * For a row whose provider is NULL — NOT RECORDED, never "some other provider".
 *
 * Naming the gap beats labelling the value with a brand we cannot prove carried it: the
 * per-leg provider columns are nullable, are not backfilled, and every historical row is
 * therefore NULL forever.
 */
export const OUTBOUND_HANDLE_UNKNOWN = `Outbound handle (provider ${PROVIDER_NOT_RECORDED})`

/** The heading under which this provider's outbound handle may be shown. */
export function outboundHandleLabel(provider: string | null | undefined): string {
  const raw = (provider || '').trim().toLowerCase()
  if (!raw) return OUTBOUND_HANDLE_UNKNOWN
  const known = OUTBOUND_HANDLE_LABELS[raw]
  if (known) return known
  // A provider we ship but have no entry for: show its own name rather than another's, so
  // the gap is self-evident instead of being filled in wrongly.
  return `${providerLabel(raw)} outbound handle`
}

/**
 * Whether this transmission's outbound status can be refreshed from eFax's metadata endpoint.
 *
 * THE T9.2 DISPATCH, and it is deliberately NOT `Boolean(row.efax_fax_id)` — the test it
 * replaces. Both providers store their handle in that one column, so its truthiness says
 * nothing about WHOSE id it is: every SFax run passed that test and was sent to eFax's
 * `/faxes/{id}/metadata`, which answered about an identifier minted by a different platform.
 * Only the provider and the transport can decide, and both come from the row.
 */
export function canQueryEfaxMetadata(
  provider: string | null | undefined,
  transport: string | null | undefined,
  handle: string | null | undefined,
): boolean {
  const p = (provider || '').trim().toLowerCase()
  if (!handle) return false
  // 'eapi' and 'api' are the same transport under two vocabularies (the job column's and the
  // registry's); accepting both means neither has to be migrated into the other.
  return p === 'efax' && isApiTransport(transport)
}

/**
 * Whether this transmission's outbound status can be refreshed from SRFax's `Get_FaxStatus`.
 *
 * SRFax's API leg keys that endpoint on the `FaxDetailsID` `Queue_Fax` returned, which is
 * stored in the same `efax_fax_id` column — so this asks the row's PROVIDER, exactly as its
 * eFax sibling does, and never the truthiness of the handle.
 *
 * THE SMTP LEG IS EXCLUDED BY THE TRANSPORT CHECK, and that is not a detail. SRFax's fax-by-
 * email gateway returns no FaxDetailsID at all, so `Get_FaxStatus` — which is keyed on one —
 * cannot be called for such a transmission. Its provider module says so, and an Info button
 * that tried anyway would report a lookup failure for a run that is behaving correctly.
 */
export function canQuerySrfaxStatus(
  provider: string | null | undefined,
  transport: string | null | undefined,
  handle: string | null | undefined,
): boolean {
  const p = (provider || '').trim().toLowerCase()
  if (!handle) return false
  return p === 'srfax' && isApiTransport(transport)
}

/**
 * Whether this transmission's outbound status can be refreshed from SFax's `SendFaxStatus`.
 *
 * THE ARM THAT WAS MISSING, and its absence is why every SFax transmission opened the
 * local-record panel: the predicate below answered `false` for it, so the client never called
 * `check-status` — and even a client that had called it would have met a refusal, because the
 * endpoint had no SFax branch either. Both halves of the lookup had shipped and were already
 * driven by the background poller (`fetch_send_fax_status` + `apply_sfax_outbound_status`);
 * what was missing was the dispatch at each end.
 *
 * SFax keys `SendFaxStatus` on the `SendFaxQueueId` its `SendFax` accept returned, which is
 * stored in the same `efax_fax_id` column — so this asks the row's PROVIDER exactly as its two
 * siblings do, and never the truthiness of the handle. That truthiness test is the defect this
 * whole file replaces: it routed every SFax run to eFax's metadata endpoint.
 *
 * THE SMTP LEG IS EXCLUDED BY THE TRANSPORT CHECK. An SFax fax sent through its mail gateway
 * has no `SendFaxQueueId`, so there is nothing to key the query on, and a button that tried
 * anyway would report a lookup failure for a transmission behaving correctly.
 */
export function canQuerySfaxStatus(
  provider: string | null | undefined,
  transport: string | null | undefined,
  handle: string | null | undefined,
): boolean {
  const p = (provider || '').trim().toLowerCase()
  if (!handle) return false
  return p === 'sfax' && isApiTransport(transport)
}

/**
 * Whether a live provider lookup exists for this leg at all — the ONE question the Info
 * button asks.
 *
 * A SINGLE PREDICATE RATHER THAN A CHAIN OF `||` AT THE CALL SITE, because the call site is
 * shared by the dashboard and the batch report and a chain assembled twice is a chain that
 * gains a provider in one place only. `POST /api/runs/{id}/check-status` dispatches on the
 * row's provider server-side; this only decides whether to CALL it.
 */
export function canRefreshOutboundStatus(
  provider: string | null | undefined,
  transport: string | null | undefined,
  handle: string | null | undefined,
): boolean {
  return (
    canQueryEfaxMetadata(provider, transport, handle) ||
    canQuerySrfaxStatus(provider, transport, handle) ||
    canQuerySfaxStatus(provider, transport, handle)
  )
}

/**
 * `'api'` and `'eapi'` are the same transport under two vocabularies — the provider registry's
 * and the stored job column's. Neither is migrated into the other, so every reader has to
 * accept both; written once here so a new one cannot accept only half the rows.
 */
function isApiTransport(transport: string | null | undefined): boolean {
  const t = (transport || '').trim().toLowerCase()
  return t === 'api' || t === 'eapi'
}
